import { MatCheckboxChange } from '@angular/material/checkbox';
import { NavigationExtras, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { catchError, finalize, of } from 'rxjs';
import { PropertyListingDto, ListingLifeCycleState, MultiplePropertyListingDto, RoleDto, UserDto, AgentListingDto } from '../../../../app/services/property-matrixV2/models';
import { ListingGroupStateService } from '../../../../app/shared/services/listing-group-state-service/listing-group-state.service';
import { PropertyListingService, UserManagementService } from '../../../../app/services/property-matrixV2/services';
import { ListingStateService } from '../../../../app/shared/services/listing-state-service/listing-state.service';
import { NotificationService } from '../../../../app/shared/services/notification-service/notification.service';
import { KeyValuePair } from '../../../../app/common/global-models/key-value-pair';
import { CreateGroupComponent } from './create-group/create-group.component';
import { GlobalMethods } from '../../../../app/common/global-methods';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
  selector: 'app-admin-listings',
  templateUrl: './admin-listings.component.html',
  styleUrls: ['./admin-listings.component.scss', '../../../../css/2-modules/_admin-portal.scss']
})
export class AdminListingsComponent implements OnInit   {

  propertyListings: PropertyListingDto[];
  propertyListingGroups: MultiplePropertyListingDto[];
  filteredListings: PropertyListingDto[];

  isSideMenuVisible: boolean = true;
  currentHeading: string = '';
  currentDescription: string = '';
  reportTypeIndex: number = 1;
  currentUserIndex: number = 1;
  selectedListingIds: string[] = [];
  groupName: string;
  activeUsers: UserDto[] = [];
  reportReferenceNumber: string;
  menuType: string = 'listings';
  singleListing: string = 'single';
  multipleListing: string = 'multiple';
  reportTypeTabNumber: number = 1;
  currentUserWithActivetask: UserDto;
  currentUser: UserDto;
  roles: RoleDto[] = [];
  stateNumber: number = 1;
  agentListingDtoList: AgentListingDto[] = [];
  subAgentListings: PropertyListingDto[] = [];
  loading: boolean = false;
  allCompanyListings: boolean = false;
  isPrincipalAgent: boolean = false;

  // Pagination properties
  pageSize: number = 20;
  displayedListings: PropertyListingDto[] = [];
  loadingMore: boolean = false;

  reportTypes: KeyValuePair[] = [
    { key: 1, value: 'Single', icon: "home" },
    { key: 2, value: 'Multiple', icon: "holiday_village" }
  ];

  users: any = [
  ];

  constructor(
    private router: Router,
    private listingService: PropertyListingService,
    private listingStateService: ListingStateService,
    private listingGroupStateService: ListingGroupStateService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private userService: UserManagementService,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (this.loadingMore || !this.filteredListings) return;

    const scrollPosition = window.innerHeight + window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;

    // Load more when user is within 200px of the bottom
    if (scrollPosition >= documentHeight - 200) {
      this.loadMore();
    }
  }

  ngOnInit(): void {
    GlobalMethods.scrollToTop();
    this.getUsers();
    this.getMultiplePropertyListings();
    this.getRoles();
    this.getCurrentUser();
  }

  get hasMoreListings(): boolean {
    return this.filteredListings && this.displayedListings.length < this.filteredListings.length;
  }

  loadMore(): void {
    if (!this.hasMoreListings || this.loadingMore) return;

    this.loadingMore = true;
    const currentLength = this.displayedListings.length;
    const nextBatch = this.filteredListings.slice(currentLength, currentLength + this.pageSize);
    this.displayedListings = [...this.displayedListings, ...nextBatch];
    this.loadingMore = false;
  }

  private updateDisplayedListings(): void {
    if (this.filteredListings) {
      this.displayedListings = this.filteredListings.slice(0, this.pageSize);
    } else {
      this.displayedListings = [];
    }
  }

  private getRoles() {
    this.userService.apiV1UserManagementGetRolesGet().subscribe(result => {
      this.roles = result;
    });
  }

  toggleSideMenu() {
    this.isSideMenuVisible = !this.isSideMenuVisible;
  }

  onSideMenuChange(change: { heading: string, description: string, state: ListingLifeCycleState, key: number }): void {
    GlobalMethods.scrollToTop();
    this.currentHeading = change.heading ?? '';
    this.currentDescription = change.description;
    this.filterListingsByState(change.state);
    this.getPropertyListings(change.key);
    this.stateNumber = change.key;
    this.changeDetectorRef.detectChanges();
  }

  onReportTypeChange(item: number): void {
    this.reportTypeIndex = item;
  }

  private getPropertyListings(state: number = 1): void {
    this.loading = true;
    if (state == 1) {
      this.listingService.apiV1PropertyListingGetAllPropertyListingForUserAndMyTaskGet({ userId: null }).subscribe({
        next: (result: PropertyListingDto[]) => {
          this.propertyListings = result;
          this.setup();
          this.loading = false;
        },
        error: (_error) => {
          this.loading = false;
          this.notificationService.showErrorMessage('Error', 'Could not load property listings.');
        }
      });
    }
    else if (state == 2) {
      this.listingService.apiV1PropertyListingGetAllPropertyListingsGet({ allCompanyListings: this.allCompanyListings }).subscribe({
        next: (result: PropertyListingDto[]) => {
          this.propertyListings = result;
          this.setup();
          this.loading = false;
        },
        error: (_error) => {
          this.loading = false;
          this.notificationService.showErrorMessage('Error', 'Could not load property listings.');
        }
      });
    } else if (state == 7) {
      if (!this.currentUser) {
        this.notificationService.showWarningMessage("Warning", "Current principal user to get agent listings is not found.");
        return;
      }
      this.loading = true;
      this.listingService.apiV1PropertyListingGetAgentsWithPropertyListingForPrincipleUserIdGet({ principleId: this.currentUser.id })
        .pipe(
          catchError((err) =>
            of(this.notificationService.showErrorMessage("Error", "Could not get principal sub agent listings.")
            )
          ),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((result: AgentListingDto[] | void) => {
          if (result && result.length > 0) {
            this.subAgentListings = [];
            this.agentListingDtoList = result;
            for (let index = 0; index < this.agentListingDtoList.length; index++) {
              const element = this.agentListingDtoList[index];
              if (element.propertyListings) {
                element.propertyListings.forEach(element => {
                  this.subAgentListings.push(element);
                });
              }
            }
            this.propertyListings = this.subAgentListings;
            this.setup();
          } else {
            this.notificationService.showWarningMessage("Warning", "No agent listings found for the principal user.");
          }
        });
    }
    else {
      this.listingService.apiV1PropertyListingGetAllPropertyListingsGet({ isDeleted: true }).subscribe({
        next: (result: PropertyListingDto[]) => {
          this.propertyListings = result.filter(f => f.isDeleted == true);
          this.setup(state);
          this.loading = false;
        },
        error: (_error) => {
          this.loading = false;
          this.notificationService.showErrorMessage('Error', 'Could not load property listings.');
        }
      });
    }
  }

  private setup(state: number = 1) {
    if (this.propertyListings.length > 0) {
      let count = 0;
      this.propertyListings.forEach(listing => {
        count++;
        const roleColor = '#' + Math.floor(Math.random() * 0x7FFFFF).toString(16).padStart(6, '0');
        if (listing.taskList && listing.taskList[0]?.user == null) {
          listing.taskList[0].user = this.activeUsers.find(f => f.userRoles?.some(a => a.roleName === 'Planner Portal Administrator')
            || f.userRoles?.some(a => a.roleName === 'Planner Portal User') && f.id == this.currentUser.id);
          this.users.push({
            key: count,
            name: listing.taskList[0].user.name + ' ' + listing.taskList[0].user.surname,
            role: listing.taskList[0].user.userRoles[0].roleName,
            roleInitial: listing.taskList[0].user.userRoles[0].roleName.charAt(0),
            roleColor: roleColor
          });
        }
        else {
          this.currentUserWithActivetask = listing.taskList.find(f => f.taskStatus.taskStatusNumber.toString().startsWith('2'))?.user
            || listing.taskList.find(f => f.taskStatus.taskStatusNumber.toString().startsWith('3'))?.user;
          if (this.currentUserWithActivetask == null) {
            this.currentUserWithActivetask = listing.taskList[0].user;
          }
          if (this.currentUserWithActivetask.userRoles[0].roleName == null) {
            this.currentUserWithActivetask.userRoles[0].roleName = this.roles.find(f => f.id == this.currentUserWithActivetask.userRoles[0].roleid).name;
          }
          this.users.push({
            key: count,
            name: this.currentUserWithActivetask.name + ' ' + this.currentUserWithActivetask.surname,
            role: this.currentUserWithActivetask.userRoles[0].roleName,
            roleInitial: this.currentUserWithActivetask.userRoles[0]?.roleName?.charAt(0) || '',
            roleColor: roleColor
          });
        }
      });
    }
    this.filterBasedOnSelection('All');
  }
  private getMultiplePropertyListings(): void {
    this.loading = true;
    this.listingService.apiV1PropertyListingGetAllMultiplePropertyListingsGet().subscribe({
      next: (result: MultiplePropertyListingDto[]) => {
        this.propertyListingGroups = result;
        this.loading = false;
      },
      error: (_error) => {
        this.loading = false;
        this.notificationService.showErrorMessage('Error', 'Could not load property listing groups.');
      }
    });
  }

  onListingCardSelected(listingId: string) {
    if (!this.selectedListingIds.includes(listingId)) {
      this.selectedListingIds.push(listingId);
    } else {
      const index = this.selectedListingIds.indexOf(listingId);
      if (index > -1) {
        this.selectedListingIds.splice(index, 1);
      }
    }
  }

  onCreateGroup() {
    const dialogRef = this.dialog.open(CreateGroupComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.groupName = result.groupName;
        this.saveMultiplePropertyListing();
      }
    });
  }

  isCreateGroupButtonVisible() {
    return this.selectedListingIds?.length > 0;
  }

  get propertyListingGroupToSave(): MultiplePropertyListingDto {
    return {
      propertyListingIds: this.selectedListingIds,
      groupName: this.groupName,
      coverImage: this.propertyListingGroups[0]?.coverImage,
      lifeCycleState: ListingLifeCycleState.Assigned,
      reportReferenceNumber: this.reportReferenceNumber,
      currentUser: this.users[this.currentUserIndex - 1],
      createdDate: new Date().toISOString(),
    };
  }

  saveMultiplePropertyListing(): void {
    this.listingService.apiV1PropertyListingAddOrUpdateMultiplePropertyListingPost({
      body: this.propertyListingGroupToSave
    }).subscribe({
      next: () => {
        this.notificationService.showSuccessMessage('Success', 'Successfully saved property group details.');
      },
      error: (_error) => {
        this.notificationService.showErrorMessage('Error', 'Could not save property group details.');
      }
    });
  }

  goToListingOverview(listing: PropertyListingDto): void {
    this.listingStateService.setSelectedListing(listing);
    let navigationExtras: NavigationExtras = {
      queryParams: {
        'listingType': this.singleListing
      }
    };
    this.router.navigate(['admin/listings/listing-overview'], navigationExtras);
  }

  goToListingOverviewNewWindow(listing: PropertyListingDto): void {
    this.listingStateService.setSelectedListing(listing);
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/admin/listings/listing-overview'], {
        queryParams: {
          listingType: this.singleListing
        }
      })
    );

    window.open('#/' + url, '_blank');
  }

  goToListingGroupOverview(group: MultiplePropertyListingDto): void {
    this.listingGroupStateService.setSelectedListingGroup(group);
    let navigationExtras: NavigationExtras = {
      queryParams: {
        'listingType': this.multipleListing
      }
    };
    this.router.navigate(['admin/listings/listing-overview'], navigationExtras);
  }

  getUserIndex(listingIndex: number): number {
    return (listingIndex % this.users.length) + 1;
  }

  navigateToReportTypeTab(index: number) {
    this.reportTypeTabNumber = index;
    GlobalMethods.scrollToTop();
  }

  filterListingsByState(state: ListingLifeCycleState): void {
    if (this.propertyListings) {
      this.filteredListings = this.propertyListings.filter(listing => listing.lifeCycleState === state);
    }
  }

  searchListings(event: any): void {
    const searchStr = event.target.value.trim().toLowerCase();

    if (!searchStr) {
      this.filteredListings = this.propertyListings;
      this.updateDisplayedListings();
      return;
    }

    this.filteredListings = this.propertyListings.filter(listing =>
      listing.propertyDetail?.propertyDescription?.toLowerCase().includes(searchStr) ||
      listing.propertyDetail?.propertyAddress?.toLowerCase().includes(searchStr) ||
      listing.propertyDetail?.addressDetail?.streetNumber?.toLowerCase().includes(searchStr) ||
      listing.propertyDetail?.addressDetail?.streetName?.toLowerCase().includes(searchStr) ||
      listing.propertyDetail?.addressDetail?.suburb?.toLowerCase().includes(searchStr) ||
      listing.propertyDetail?.addressDetail?.city?.toLowerCase().includes(searchStr) ||
      listing.propertyDetail?.addressDetail?.complexName?.toLowerCase().includes(searchStr) ||
      listing.surveyorGeneralKey?.toLowerCase().includes(searchStr) ||
      listing.planningReportId?.toLowerCase().includes(searchStr) ||
      listing.taskList?.forEach(f => f.user?.name.toLowerCase().includes(searchStr)) ||
      listing.taskList?.forEach(f => f.user?.surname?.toLowerCase().includes(searchStr))
    );
    this.updateDisplayedListings();
  }
  private getUsers(): void {
    this.userService.apiV1UserManagementGetUsersGet().subscribe(user => {
      this.activeUsers = user;
      this.getPropertyListings();
    });
  }

  private getCurrentUser(): void {
    this.userService.apiV1UserManagementGetCurrentUserGet().subscribe(result => {
      if (result != null) {
        this.currentUser = result;
        if (this.currentUser.userRoles?.some(r => r.roleName === 'Principal Agent')) {
          this.isPrincipalAgent = true;
        }
      }
      else {
        this.notificationService.showWarningMessage('No Current User', 'No current user found.');
      }
    });
  }

  filterListingBasedOnTab(event: MatTabChangeEvent) {
    switch (event.index) {
      case 1:
        this.filterBasedOnSelection('Published');
        break;
      case 2:
        this.filterBasedOnSelection('UnderOffer');
        break;
      case 3:
        this.filterBasedOnSelection('Sold');
        break;
      default:
        this.filterBasedOnSelection("All");
        break;
    }
  }

  filterBasedOnSelection(filter: string) {
    if (!this.propertyListings) return;
    if (filter === 'All') {
      this.filteredListings = this.propertyListings.filter(
        listing => listing.lifeCycleState !== ListingLifeCycleState.Published
      );
    } else {
      const state = ListingLifeCycleState[filter as keyof typeof ListingLifeCycleState];
      this.filteredListings = this.propertyListings.filter(
        listing => listing.lifeCycleState === state
      );
    }
    this.updateDisplayedListings();
  }

  onListingUpdated(): void {
    this.getPropertyListings(this.stateNumber);
  }

  loadAllCompanyListings() {
    this.allCompanyListings = !this.allCompanyListings;

    const btn = document.getElementById('company-listings-button');
    if (btn != undefined) {
      if (this.allCompanyListings) {
        btn.classList.toggle('active');
      } else {
        btn.classList.remove('active');
      }
    }

    this.getPropertyListings(this.stateNumber);
  }
}
